import * as React from 'react';
import { Breakpoint, useTheme } from '@mui/material/styles';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, SxProps, Theme, Stack } from '@mui/material';
import { ButtonPrimary, ButtonSecondary } from 'components/ui/Button/Button';
import CloseIcon from '@mui/icons-material/Close';

export type FormDialogVariants = 'primary' | 'secondary' | 'icon' | 'text';

export type FormDialogProps = {
    children: React.ReactNode;
    buttonText: string | React.ReactElement;
    title: string | React.ReactElement;
    type?: FormDialogVariants;
    showCancel?: boolean;
    submitText?: string;
    cancelText?: string;
    onSubmit?: () => Promise<void | boolean>;
    disabled?: boolean;
    showActions?: boolean;
    titleSx?: SxProps<Theme>;
    buttonSx?: SxProps<Theme>;
    maxWidth?: Breakpoint | false;
    onOpen?: () => void;
    wizardProps?: {
        changeStep: (page: number) => Promise<void>;
        nextIsDisabled: boolean;
        totalSteps: number;
        activeStep: number;
    };
};

const getButtonByType = (type: FormDialogVariants) => {
    if (type === 'primary') {
        return ButtonPrimary;
    }
    if (type === 'secondary') {
        return ButtonSecondary;
    }

    return IconButton;
};

const FormDialog = ({
    buttonText,
    title,
    children,
    type = 'primary',
    showCancel = true,
    submitText = 'Submit',
    cancelText = 'Cancel',
    onSubmit = () => new Promise<void>((resolve) => resolve()),
    disabled = false,
    showActions = true,
    buttonSx = {},
    titleSx = {},
    maxWidth = false,
    wizardProps,
    onOpen = () => {}
}: FormDialogProps) => {
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => {
        setOpen(true);
        onOpen();
    };

    const handleClose = () => {
        setOpen(false);
    };

    const ButtonType = getButtonByType(type);
    const parsedTitle = typeof title === 'string' ? title.replace(/\s+/g, '-').toLowerCase() : '-form-dialog';
    const ariaLabel = `open-${parsedTitle}-form-dialog`;

    return (
        <>
            {type === 'text' ? (
                <Button sx={buttonSx} onClick={handleClickOpen} variant="text">
                    {buttonText}
                </Button>
            ) : (
                <ButtonType sx={buttonSx} onClick={handleClickOpen}>
                    {buttonText}
                </ButtonType>
            )}
            <Dialog open={open} onClose={handleClose} aria-labelledby={ariaLabel} disableEscapeKeyDown={disabled} maxWidth={maxWidth}>
                {open && (
                    <>
                        <DialogTitle id={ariaLabel} sx={titleSx}>
                            {title}
                        </DialogTitle>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: theme.palette.grey[500]
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <DialogContent>{children}</DialogContent>
                        {showActions && !wizardProps && (
                            <DialogActions sx={{ pr: 2.5 }}>
                                {showCancel && (
                                    <Button sx={{ color: theme.palette.error.dark }} onClick={handleClose} color="secondary">
                                        {cancelText}
                                    </Button>
                                )}
                                <Button
                                    disabled={disabled}
                                    variant="contained"
                                    size="small"
                                    onClick={() => {
                                        console.log('onSubmit');
                                        onSubmit().then((success) => {
                                            if (typeof success === 'boolean' && success === true) {
                                                handleClose();
                                            } else if (typeof success === 'undefined') {
                                                handleClose();
                                            }
                                        });
                                    }}
                                >
                                    {submitText}
                                </Button>
                            </DialogActions>
                        )}
                        {wizardProps && (
                            <DialogActions sx={{ pr: 2.5 }}>
                                <Stack direction="row" justifyContent={wizardProps.activeStep !== 0 ? 'space-between' : 'flex-end'}>
                                    {wizardProps.activeStep !== 0 && (
                                        <Button onClick={() => wizardProps.changeStep(wizardProps.activeStep - 1)} sx={{ my: 3, ml: 1 }}>
                                            Back
                                        </Button>
                                    )}
                                    <Button
                                        disabled={wizardProps.nextIsDisabled}
                                        variant="contained"
                                        onClick={() => {
                                            if (wizardProps.activeStep === wizardProps.totalSteps) {
                                                onSubmit().then((success) => {
                                                    if (typeof success === 'boolean' && success === true) {
                                                        handleClose();
                                                    } else if (typeof success === 'undefined') {
                                                        handleClose();
                                                    }
                                                });
                                            } else {
                                                wizardProps.changeStep(wizardProps.activeStep + 1);
                                            }
                                        }}
                                        sx={{ my: 3, ml: 1 }}
                                    >
                                        {wizardProps.activeStep === wizardProps.totalSteps ? submitText : 'Next'}
                                    </Button>
                                </Stack>
                            </DialogActions>
                        )}
                    </>
                )}
            </Dialog>
        </>
    );
};

export default FormDialog;
